import { useCallback, useState } from 'react';
import ChatService from '../services/ChatService';

/**
 * Vernetzungsanfrage senden (Okt 2026, aus pages/ConnectPage herausgezogen).
 *
 * Genutzt vom Vernetzen-Blatt (components/connect/ConnectSheet) und weiter von
 * der ConnectPage als Rueckfall fuer direkte Aufrufe von /connect/:id.
 *
 * send() liefert { ok: true } oder { ok: false, chatId } (schon vernetzt oder
 * Kreuzanfrage zusammengeführt → in den Chat) bzw. setzt `error` mit einem verstaendlichen Satz.
 *
 * origin (optional): Herkunft der Anfrage fuer die Forschung
 * (ROUND | SEARCH | SKILL_BROWSE | PROFILE_LINK | OTHER), siehe ChatService.
 */
export const DEFAULT_CONNECT_MESSAGE = 'Ich würde gerne mit dir lernen! Können wir uns vernetzen?';

export const connectErrorMessage = (err) => {
  if (err?.status === 404) {
    return 'Diese Person ist nicht mehr verfügbar. Möglicherweise wurde das Profil gelöscht oder deaktiviert.';
  }
  if (err?.status === 409) return 'Du hast dieser Person bereits eine Vernetzungsanfrage geschickt.';
  if (err?.status === 403) {
    return 'Du kannst dieser Person keine Anfrage senden. Möglicherweise gehört sie zu einer anderen Schule.';
  }
  if (err?.status >= 500) return 'Es gab einen Server-Fehler. Bitte versuche es in einigen Minuten erneut.';
  return 'Die Anfrage konnte nicht gesendet werden. Bitte versuche es später erneut.';
};

const useConnectRequest = (origin) => {
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');

  const send = useCallback(async (recipientId, message) => {
    setError('');
    setIsSending(true);
    try {
      const text = (message || '').trim() || DEFAULT_CONNECT_MESSAGE;
      const response = origin
        ? await ChatService.sendChatRequest(recipientId, text, origin)
        : await ChatService.sendChatRequest(recipientId, text);
      // Kreuzanfrage (Okt 2026): Die andere Person hatte schon angefragt, der
      // Server hat beides zu einer Vernetzung zusammengeführt. Wie bei
      // ALREADY_CONNECTED direkt in den Chat.
      if (response?.connected && response?.chatId) {
        return { ok: false, chatId: response.chatId };
      }
      return { ok: true };
    } catch (err) {
      // Backend meldet bestehende Vernetzung mit der Chat-ID: kein Fehler,
      // sondern der Weg in den vorhandenen Chat.
      if (err?.code === 'ALREADY_CONNECTED' && err.body?.chatId) {
        return { ok: false, chatId: err.body.chatId };
      }
      setError(connectErrorMessage(err));
      return { ok: false };
    } finally {
      setIsSending(false);
    }
  }, [origin]);

  return { send, isSending, error, setError };
};

export default useConnectRequest;
